'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  RestartAlt as RestartIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';

interface Task {
  id: string;
  task_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  priority: number;
  progress: number;
  progress_message?: string;
  error_message?: string;
  retry_count: number;
  max_retries: number;
  project_id: number;
  user_id: number;
  document_id?: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  updated_at?: string;
  next_retry_at?: string;
  task_data?: string;
  result_data?: string;
}

interface TaskStats {
  total: number;
  pending: number;
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
}

interface TaskDetail extends Task {
  service_info?: any;
  is_running?: boolean;
  running_info?: any;
}

const AdminTasksPage: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const pageSize = 20;

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
      });

      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('task_type', typeFilter);

      const response = await apiClient.get(`/admin/tasks?${params}`);

      const responseData = response.data.data || response.data;

      setTasks(responseData.tasks || []);
      setTotal(responseData.total || 0);
      setStats(responseData.stats || null);
    } catch (err: any) {
      console.error('获取任务列表失败:', err);
      setError(err.response?.data?.detail || '获取任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskDetail = async (taskId: string) => {
    try {
      setDetailLoading(true);
      const response = await apiClient.get(`/admin/tasks/${taskId}`);

      const responseData = response.data.data || response.data;
      // 后端返回的数据结构是 {task: {...}}，需要提取 task 对象
      setSelectedTask(responseData.task || responseData);
    } catch (err: any) {
      console.error('获取任务详情失败:', err);
      setError(err.response?.data?.detail || '获取任务详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const cancelTask = async (taskId: string) => {
    try {
      await apiClient.post(`/admin/tasks/${taskId}/cancel`);
      // 重新获取任务列表
      fetchTasks();
      // 关闭详情对话框
      setSelectedTask(null);
    } catch (err: any) {
      console.error('取消任务失败:', err);
      setError(err.response?.data?.detail || '取消任务失败');
    }
  };

  const retryTask = async (taskId: string) => {
    try {
      await apiClient.post(`/admin/tasks/${taskId}/retry`);
      // 重新获取任务列表
      fetchTasks();
      // 关闭详情对话框
      setSelectedTask(null);
    } catch (err: any) {
      console.error('重新执行任务失败:', err);
      setError(err.response?.data?.detail || '重新执行任务失败');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'running': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ScheduleIcon />;
      case 'running': return <PlayArrowIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'cancelled': return <StopIcon />;
      default: return <InfoIcon />;
    }
  };

  const getTaskTypeLabel = (type: string) => {
    switch (type) {
      case 'report_generation': return '报告生成';
      case 'document_processing': return '文档处理';
      case 'log_extraction': return '日志提取';
      default: return type;
    }
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return new Date(timeString).toLocaleString('zh-CN');
  };

  const formatDuration = (startTime?: string, endTime?: string) => {
    if (!startTime) return '-';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = Math.floor((end.getTime() - start.getTime()) / 1000);

    if (duration < 60) return `${duration}秒`;
    if (duration < 3600) return `${Math.floor(duration / 60)}分钟`;
    return `${Math.floor(duration / 3600)}小时`;
  };

  useEffect(() => {
    fetchTasks();
  }, [page, statusFilter, typeFilter]);

  // 检查用户权限
  if (!user || !['super_admin', 'admin', 'zhaoyuan_admin'].includes(user.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          需要管理员权限才能访问此页面
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        后台任务管理
      </Typography>

      {/* 统计信息 */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  总任务数
                </Typography>
                <Typography variant="h4">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  等待中
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  运行中
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.running}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  已完成
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  失败
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.failed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  已取消
                </Typography>
                <Typography variant="h4" color="default">
                  {stats.cancelled}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 筛选和操作 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>状态筛选</InputLabel>
                <Select
                  value={statusFilter}
                  label="状态筛选"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">全部状态</MenuItem>
                  <MenuItem value="pending">等待中</MenuItem>
                  <MenuItem value="running">运行中</MenuItem>
                  <MenuItem value="completed">已完成</MenuItem>
                  <MenuItem value="failed">失败</MenuItem>
                  <MenuItem value="cancelled">已取消</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>类型筛选</InputLabel>
                <Select
                  value={typeFilter}
                  label="类型筛选"
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">全部类型</MenuItem>
                  <MenuItem value="report_generation">报告生成</MenuItem>
                  <MenuItem value="document_processing">文档处理</MenuItem>
                  <MenuItem value="log_extraction">日志提取</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchTasks}
                disabled={loading}
              >
                刷新
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 任务列表 */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>任务ID</TableCell>
                    <TableCell>类型</TableCell>
                    <TableCell>状态</TableCell>
                    <TableCell>进度</TableCell>
                    <TableCell>项目ID</TableCell>
                    <TableCell>重试次数</TableCell>
                    <TableCell>创建时间</TableCell>
                    <TableCell>开始时间</TableCell>
                    <TableCell>耗时</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Tooltip title={task.id}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {task.id.substring(0, 12)}...
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTaskTypeLabel(task.task_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(task.status)}
                          label={task.status}
                          color={getStatusColor(task.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={task.progress || 0}
                            sx={{ width: 100 }}
                          />
                          <Typography variant="body2">
                            {task.progress || 0}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{task.project_id}</TableCell>
                      <TableCell>
                        {task.retry_count}/{task.max_retries}
                      </TableCell>
                      <TableCell>{formatTime(task.created_at)}</TableCell>
                      <TableCell>{formatTime(task.started_at)}</TableCell>
                      <TableCell>
                        {formatDuration(task.started_at, task.completed_at)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="查看详情">
                          <IconButton
                            size="small"
                            onClick={() => fetchTaskDetail(task.id)}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        {task.status === 'running' && (
                          <Tooltip title="取消任务">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => cancelTask(task.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(task.status === 'failed' || task.status === 'cancelled') && (
                          <Tooltip title="重新执行">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => retryTask(task.id)}
                            >
                              <RestartIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* 分页 */}
          {!loading && total > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(total / pageSize)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 任务详情对话框 */}
      <Dialog
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>任务详情</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedTask && (
            <Box>
              <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)}>
                <Tab label="基本信息" />
                <Tab label="任务数据" />
                <Tab label="执行状态" />
              </Tabs>

              <Box sx={{ mt: 2 }}>
                {currentTab === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">任务ID:</Typography>
                      <Typography variant="body2">{selectedTask?.id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">任务类型:</Typography>
                      <Typography variant="body2">
                        {getTaskTypeLabel(selectedTask?.task_type)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">状态:</Typography>
                      <Chip
                        icon={getStatusIcon(selectedTask?.status)}
                        label={selectedTask?.status || 'unknown'}
                        color={getStatusColor(selectedTask?.status)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">优先级:</Typography>
                      <Typography variant="body2">{selectedTask?.priority || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">进度:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={selectedTask?.progress || 0}
                          sx={{ width: 100 }}
                        />
                        <Typography variant="body2">
                          {selectedTask?.progress || 0}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">项目ID:</Typography>
                      <Typography variant="body2">{selectedTask?.project_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">用户ID:</Typography>
                      <Typography variant="body2">{selectedTask?.user_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">创建时间:</Typography>
                      <Typography variant="body2">{formatTime(selectedTask?.created_at)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">开始时间:</Typography>
                      <Typography variant="body2">{formatTime(selectedTask?.started_at)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">完成时间:</Typography>
                      <Typography variant="body2">{formatTime(selectedTask?.completed_at)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">耗时:</Typography>
                      <Typography variant="body2">
                        {formatDuration(selectedTask?.started_at, selectedTask?.completed_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">重试次数:</Typography>
                      <Typography variant="body2">
                        {selectedTask?.retry_count || 0}/{selectedTask?.max_retries || 0}
                      </Typography>
                    </Grid>
                    {selectedTask.progress_message && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">进度信息:</Typography>
                        <Typography variant="body2">{selectedTask.progress_message}</Typography>
                      </Grid>
                    )}
                    {selectedTask.error_message && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="error">错误信息:</Typography>
                        <Typography variant="body2" color="error">
                          {selectedTask.error_message}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}

                {currentTab === 1 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      任务数据:
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedTask.task_data || '无任务数据'}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {currentTab === 2 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      执行状态:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          是否正在运行: {selectedTask.is_running ? '是' : '否'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          运行状态: {selectedTask.running_info?.done ? '已完成' : '运行中'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTask(null)}>关闭</Button>
          {selectedTask && selectedTask.status === 'running' && (
            <Button
              onClick={() => {
                cancelTask(selectedTask.id);
              }}
              color="error"
              startIcon={<CancelIcon />}
            >
              取消任务
            </Button>
          )}
          {selectedTask && (selectedTask.status === 'failed' || selectedTask.status === 'cancelled') && (
            <Button
              onClick={() => {
                retryTask(selectedTask.id);
              }}
              color="primary"
              startIcon={<RestartIcon />}
            >
              重新执行
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTasksPage;